import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuBar } from '@/widgets/editor';
import { createBaseEditorExtensions } from '@/widgets/editor/core/editorExtensions.js';
import '@/widgets/editor/editorStyles.css';

const DEFAULT_DEBOUNCE_MS = 200;

const PROSEMIRROR_CLASS =
  'prose prose-sm max-w-none w-full text-left focus:outline-none p-4 dark:prose-invert';

export type RichTextFieldProps = {
  value?: string | null;
  onChange: (html: string) => void;
  onBlur?: (html: string) => void;
  placeholder?: string;
  className?: string;
  /** Applied to the ProseMirror contenteditable (e.g. `min-h-[360px]` for a tall click target). */
  editorClassName?: string;
  /** Merged onto the editor shell (border/background wrapper around ProseMirror). */
  shellClassName?: string;
  disabled?: boolean;
  'data-testid'?: string;
  debounceMs?: number;
  editorTestId?: string;
  /**
   * Keep the formatting toolbar visible while scrolling long content.
   * Pins the toolbar to the top of the field and scrolls the editor body.
   */
  stickyToolbar?: boolean;
  /** Always render the formatting toolbar (without the sticky split layout). */
  persistToolbar?: boolean;
};

function normalizeRichTextValue(value: string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  return String(value);
}

/**
 * Controlled rich-text field with `value` / `onChange`, debounced parent sync while typing,
 * and immediate flush on blur. Reuses TipTap toolbar and extensions from `widgets/editor`.
 */
export function RichTextField({
  value = null,
  onChange,
  onBlur,
  placeholder,
  className = '',
  editorClassName = '',
  shellClassName = '',
  disabled = false,
  'data-testid': dataTestId = 'components-rich-text-field',
  debounceMs = DEFAULT_DEBOUNCE_MS,
  editorTestId,
  stickyToolbar = false,
  persistToolbar = false,
}: RichTextFieldProps) {
  const { t } = useTranslation();
  const [showMenuBar, setShowMenuBar] = useState(stickyToolbar || persistToolbar);
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  onChangeRef.current = onChange;
  onBlurRef.current = onBlur;

  const flushPendingChange = useCallback((html: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    onChangeRef.current(html);
  }, []);

  const scheduleChange = useCallback(
    (html: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
        onChangeRef.current(html);
      }, debounceMs);
    },
    [debounceMs]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const proseMirrorClass = useMemo(
    () => [PROSEMIRROR_CLASS, editorClassName].filter(Boolean).join(' '),
    [editorClassName]
  );

  const handleEditorKeyDown = useCallback((_view: unknown, event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setShowMenuBar(true);
      setShowLinkPopover(true);
      return true;
    }
    return false;
  }, []);

  const editor = useEditor(
    {
      content: normalizeRichTextValue(value) || null,
      editable: !disabled,
      editorProps: {
        attributes: {
          class: proseMirrorClass,
          ...(editorTestId ? { 'data-testid': editorTestId } : {}),
        },
        handleKeyDown: handleEditorKeyDown,
      },
      extensions: [
        ...createBaseEditorExtensions({ openLinkOnClick: false }),
        Placeholder.configure({
          placeholder:
            placeholder !== undefined ? placeholder : t('oneOnOne.meeting.content.placeholder'),
        }),
      ],
      onFocus() {
        setShowMenuBar(true);
      },
      onUpdate({ editor: activeEditor }) {
        scheduleChange(activeEditor.getHTML());
      },
      onBlur({ editor: activeEditor }) {
        setShowLinkPopover(false);
        if (disabled) {
          return;
        }
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        const html = activeEditor.getHTML();
        flushPendingChange(html);
        onBlurRef.current?.(html);
      },
    },
    [editorTestId, proseMirrorClass]
  );

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || editor.isFocused) {
      return;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    const next = normalizeRichTextValue(value);
    const current = editor.getHTML();
    const normalizedCurrent = current === '<p></p>' || current === '<p><br></p>' ? '' : current;
    const normalizedNext = next === '<p></p>' || next === '<p><br></p>' ? '' : next;
    if (normalizedNext !== normalizedCurrent && next !== current) {
      editor.commands.setContent(next || '', false);
    }
  }, [editor, value]);

  const rootClassName = [
    'flex w-full flex-col text-left',
    stickyToolbar ? 'h-full min-h-0 flex-1 overflow-hidden' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const editorShellClassName = [
    'rich-text-field-editor w-full rounded-lg',
    stickyToolbar ? 'mt-0 mb-0' : 'mt-2 mb-2 w-full',
    disabled
      ? 'border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-100'
      : 'bg-white dark:bg-slate-900/50 dark:text-slate-100',
    shellClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const shouldShowMenuBar = showMenuBar || stickyToolbar || persistToolbar;

  const menuBar = shouldShowMenuBar ? (
    <MenuBar
      editor={editor}
      setShowLinkPopover={setShowLinkPopover}
      showLinkPopover={showLinkPopover}
      t={t}
    />
  ) : null;

  if (stickyToolbar) {
    return (
      <div className={rootClassName} data-testid={dataTestId}>
        <div
          className="rich-text-field-sticky-toolbar z-10 flex w-full shrink-0 flex-col items-start rounded-t-lg border-b border-slate-200 bg-white px-1 py-1 text-left dark:border-slate-700 dark:bg-slate-900"
          data-testid="components-rich-text-field-sticky-toolbar"
        >
          {menuBar}
        </div>
        <div className="rich-text-field-sticky-body min-h-0 flex-1 overflow-y-auto">
          <EditorContent
            className={editorShellClassName}
            data-testid="components-rich-text-field-content"
            editor={editor}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName} data-testid={dataTestId}>
      {menuBar}
      <EditorContent
        className={editorShellClassName}
        data-testid="components-rich-text-field-content"
        editor={editor}
      />
    </div>
  );
}
